/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { Box, IconButton, Tooltip, Typography, TableCellProps } from '@mui/material';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import {LuiActionsMenu, LuiMaterialTableHeadCellStyles} from '@jda/lui-common-component-library-mui5';

import { useStyles } from './styles';
import useWindowDimensions from '../../../utility/useWindowDimensions';
import { EMAIL, LABELS_ROLES, NA, ROWS_PER_PAGE_LABEL } from '../constants';
import { IUserTableProps } from '../../../models/UserGroups/UserGroupsContract';
import CustomTablePagination from '../../../shared/CustomTablePagination/CustomTablePagination';

const UsersTable = ({
  usersByGroup,
  pageDetails,
  loading,
  handleChangePageOptions,
  onUserDelete,
  deleteUserPermission,
  selectedRows,
  setSelectedRows,
  extendedToolbar,
  renderEmptyState
}: IUserTableProps) => {
  const { classes } = useStyles();
  const { height } = useWindowDimensions();

  const columns = [
    {
      header: LABELS_ROLES.GROUP_NAME,
      accessorKey: 'displayName',
      muiTableHeadCellProps: ({ column, table }) =>
              ({
                sx: theme => ({
                  ...LuiMaterialTableHeadCellStyles({
                    column,
                    theme,
                    table,
                    styling: 'standard',
                    virtualizationEnabled: false
                  }),
                  width: '60%'
                })
              } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.displayName}
          placement="bottom"
          disableHoverListener={!row.original.displayName}
          disableFocusListener={!row.original.displayName}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.displayName || NA}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: EMAIL,
      accessorKey: 'Email',
      muiTableHeadCellProps: ({ column, table }) =>
              ({
                sx: theme => ({
                  ...LuiMaterialTableHeadCellStyles({
                    column,
                    theme,
                    table,
                    styling: 'standard',
                    virtualizationEnabled: false
                  }),
                  width: '30%'
                })
              } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          placement="bottom"
          title={row.original.email}
          disableHoverListener={!row.original.email}
          disableFocusListener={!row.original.email}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.email || NA}
          </Typography>
        </Tooltip>
      )
    }
  ];

  const listOfUsers: any = usersByGroup.map((user: any) => {
    return { id: user.subjectId, ...user };
  });
const customRowActions: any = (row: MRT_Row<any>) => {
    return (
      <LuiActionsMenu
        items={[
          deleteUserPermission
            ? {
                render: (
                  <Box data-testid="toolTip-sign">
                    <IconButton
                      aria-label="delete"
                      data-testid="deleteIcon"
                      onClick={() => onUserDelete([row.original])}
                    >
                      <Trash className={classes.trashIcon} />
                    </IconButton>
                  </Box>
                ),
                alwaysVisible: true
              }
            : []
        ].flat()}
        maxItemsToDisplay={1}
      />
    );
  };
  return (
    <Box className={classes.root}>
      <Box>
        <CustomTablePagination
          keyAccessor={"id"}
          columns={columns}
          loading={loading}
          data={listOfUsers}
          selectedRowsData={selectedRows}
          maxTableBodyHeight={height - 350}
          rowsPerPageOptions={[20, 50, 100]}
          data-testid="user-group-role-table"
          totalCount={pageDetails.totalCount}
          labelRowsPerPage={ROWS_PER_PAGE_LABEL}
          onSelectedRowsDataChange={setSelectedRows}
          currentPageNumber={pageDetails.pageNumber}
          currentItemsPerPage={pageDetails.pageSize}
          onChangePageOptions={handleChangePageOptions}
          renderTopToolbar={extendedToolbar}
          customRowActions={customRowActions}
          renderEmptyState={renderEmptyState}
        />
      </Box>
    </Box>
  );
};

export default UsersTable;
